import { Fragment, useState } from "react";
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  createColumnHelper,
  SortingState,
} from "@tanstack/react-table";
import { ICollectionOption } from "../../models/CollectionOption";
import { resolveIcon } from "./collectionOptionConfig";

export default function CollectionOptionTable({
  collectionOptions,
  setCollectionOption,
}: {
  collectionOptions: ICollectionOption[];
  setCollectionOption: (option: ICollectionOption) => void;
}) {
  const [sorting, setSorting] = useState<SortingState>([]);

  const colHelper = createColumnHelper<ICollectionOption>();
  const cols = [
    colHelper.display({
      id: "iconPreview",
      header: "",
      cell: (info) => {
        const IconComp = resolveIcon(info.row.original.icon || "");
        const color = info.row.original.color || "bg-neutral-200 text-neutral-700";
        return (
          <span className={`inline-flex items-center justify-center h-8 w-8 rounded-lg ${color}`}>
            <IconComp className="h-4 w-4" />
          </span>
        );
      },
    }),
    colHelper.accessor("name", {
      header: "Name",
    }),
    colHelper.accessor("description", {
      header: "Description",
      cell: (info) => (
        <span className="truncate max-w-xs block">{info.getValue()}</span>
      ),
    }),
    colHelper.display({
      id: "price",
      header: "Price Adjustment",
      cell: (info) => {
        const price = info.row.original.price ?? 0;
        const formatted = `${price >= 0 ? "+" : ""}£${Math.abs(price).toFixed(2)}`;
        return (
          <span className={`font-medium ${price >= 0 ? "text-green-700" : "text-red-700"}`}>
            {formatted}
          </span>
        );
      },
    }),
    colHelper.display({
      id: "isActive",
      header: "Active",
      cell: (info) => (
        <span
          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
            info.row.original.isActive
              ? "bg-green-100 text-green-800"
              : "bg-red-100 text-red-800"
          }`}
        >
          {info.row.original.isActive ? "Active" : "Inactive"}
        </span>
      ),
    }),
  ];

  const table = useReactTable({
    data: collectionOptions,
    columns: cols,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <div className="mt-4 bg-white overflow-auto">
      <table className="min-w-full overflow-scroll divide-y border divide-gray-300">
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id} className="bg-gp-blue-50 text-gray-800">
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  scope="col"
                  className="py-3.5 px-3 text-sm font-semibold lg:table-cell text-left"
                >
                  {header.isPlaceholder ? null : (
                    <div
                      {...{
                        className: header.column.getCanSort()
                          ? "cursor-pointer select-none"
                          : "",
                        onClick: header.column.getToggleSortingHandler(),
                      }}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                      {{
                        asc: " ▲",
                        desc: " ▼",
                      }[header.column.getIsSorted() as string] ?? null}
                    </div>
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row, i) => (
            <Fragment key={i}>
              <tr
                key={row.id}
                onClick={() => setCollectionOption(row.original)}
                className="hover:bg-gray-100 hover:cursor-pointer"
              >
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className="px-3 py-3 border text-sm">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}
